import React from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal } from "../../../components/ui";
import { useClientsStore } from "./use-clients-store";
import { deleteClientSchema, type DeleteClientForm } from "./clients-types";

const Title = styled.h3`
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: #c62828;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  resize: vertical;
  outline: none;
  font-family: inherit;
  &:focus {
    border-color: #c62828;
  }
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #c62828;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const Btn = styled.button<{ $variant: "primary" | "secondary" }>`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.$variant === "primary" ? "#c62828" : "#e0e0e0")};
  background: ${(p) => (p.$variant === "primary" ? "#c62828" : "white")};
  color: ${(p) => (p.$variant === "primary" ? "white" : "#333")};
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const ModalDeleteClient: React.FC = () => {
  const { selectedClient, modalDelete, setModalDelete, deleteClient } =
    useClientsStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DeleteClientForm>({
    resolver: zodResolver(deleteClientSchema),
  });

  if (!modalDelete || !selectedClient) return null;

  const onSubmit = async (data: DeleteClientForm) => {
    await deleteClient(selectedClient.uuid, data.reason);
  };

  const clientName =
    selectedClient.profile?.first_name || selectedClient.email || selectedClient.uuid;

  return (
    <Modal
      handleClose={() => setModalDelete(false)}
      dialogStyle={{ maxWidth: 440 }}
    >
      <Title>Удалить пользователя</Title>
      <p style={{ fontSize: 13, color: "#666", margin: "0 0 12px" }}>
        Вы собираетесь удалить <strong>{clientName}</strong>. Это действие можно
        отменить.
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Label>Причина удаления</Label>
        <TextArea
          {...register("reason")}
          placeholder="Укажите причину удаления..."
        />
        {errors.reason && <ErrorText>{errors.reason.message}</ErrorText>}
        <ButtonRow>
          <Btn
            $variant="secondary"
            type="button"
            onClick={() => setModalDelete(false)}
          >
            Отмена
          </Btn>
          <Btn $variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Удаление..." : "Удалить"}
          </Btn>
        </ButtonRow>
      </form>
    </Modal>
  );
};
